import * as React from "react";
import {useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import relativeTime from "dayjs/plugin/relativeTime";
import {throttle} from "lodash";
import i18n from "../i18n/i18n";
import saveAs from "file-saver";

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

export const formatTimeTodayOrYesterday = (time, withTime = false) => {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { t } = useTranslation();

	let timeString;
	if (dayjs(time).isToday()) {
		timeString = `${t("Today")} ${t("at")} ${dayjs(time).format("HH:mm")}`;
	}
	else if (dayjs(time).isYesterday()) {
		timeString = `${t("Yesterday")} ${t("at")} ${dayjs(time).format("HH:mm")}`;
	}
	else {
		timeString = withTime ? dayjs(time).format("DD/MM/YYYY") : dayjs(time).format("DD/MM/YYYY");
	}
	return timeString;
};

export const formatTimeFull = (time) => dayjs(time).format("dddd D MMMM YYYY HH:mm");
export const formatTimeShort = (time) => dayjs(time).format("HH:mm DD/MM/YYYY");

export const useVerticalScrollDirection = () => {
	const [direction, setDirection] = React.useState("up");

	let prevScrollY = 0;

	React.useEffect(() => {
		// Using lodash, we set a throttle to the scroll event
		// making it not fire more than once every 500 ms.
		window.onscroll = throttle(() => {

			// This value keeps the latest scrollY position
			const { scrollY } = window;

			// Checks if previous scrollY is less than latest scrollY
			// If true, we are scrolling downwards, else scrolling upwards
			const direction = prevScrollY < scrollY ? "down" : "up";

			// Updates the previous scroll variable AFTER the direction is set.
			// The order of events is key to making this work, as assigning
			// the previous scroll before checking the direction will result
			// in the direction always being 'up'.
			prevScrollY = scrollY;

			// Set the state to trigger re-rendering
			setDirection(direction);
		}, 500);

		return () => {
			// Remove scroll event on unmount
			window.onscroll = null;
		};
	}, []);

	return direction;
};

export const usePreviousRefValue = (value) => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};
export const generateStaticColorFromUserIdAppCode = (userId = "") => {
	const saturation = 30;
	const lightness = 50;

	let hash = 0;
	if (userId === "") userId = "Ymmot";
	for (let i = 0; i < String(userId).length; i++) {
		// eslint-disable-next-line no-bitwise
		hash = String(userId).charCodeAt(i) + ((hash << 5) - hash);
	}
	const hue = hash % 360;
	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const generateStaticColorFromUserId = (userId, initials = "", saturation = 30, lightness = 50) => {
	// console.log("userId: ", userId);
	// console.log("initials: ", initials);
	let hash = 0;
	if (!userId && initials) {
		for (let i = 0; i < String(initials).length; i++) {
			// eslint-disable-next-line no-bitwise
			hash = String(initials).charCodeAt(i) + ((hash << 5) - hash);
		}
	}
	else {
		for (let i = 0; i < String(userId).length; i++) {
			// eslint-disable-next-line no-bitwise
			hash = String(userId).charCodeAt(i) + ((hash << 5) - hash);
		}
	}
	const hue = hash % 360;
	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const generateStaticUserStringFromUser = (user) => {
	// console.log(user);
	if (user && (user.firstName !== undefined || user.lastName !== undefined)) {
		return user?.firstName + " " + user?.lastName;
	}
	else {
		return user?.username ?? user?.emailAdress ?? user?.phoneNumber ?? "Ukjent bruker...";
	}
};

export const relativeTimeFormat = (time) => {
	// Accepts a timeString, and returns:
	// {count} second(s)/minute(s)/hour(s)/day(s)/month(s)/year(s) "from now"/"ago" or Just now.

	// in milliseconds
	const units = {
		year: 24 * 60 * 60 * 1000 * 365,
		month: 24 * 60 * 60 * 1000 * 365 / 12,
		day: 24 * 60 * 60 * 1000,
		hour: 60 * 60 * 1000,
		minute: 60 * 1000,
		second: 1000
	};

	// To change the language format, change the first variable in the function call below.
	const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });

	const getRelativeTime = (d1, d2 = new Date()) => {
		const elapsed = d1 - d2;

		// "Math.abs" accounts for both "past" & "future" scenarios
		for (const u in units) {
			if (Math.abs(elapsed) > units[u] || u === "second") { return rtf.format(Math.round(elapsed / units[u]), u); }
		}
	};

	return getRelativeTime(Number(new Date(time)));
};

export const filterArrayObjectValues = (array, keysToSearch, searchString, strict = true) => {
	if (searchString === "") {
		return array;
	}
	else {
		return array.filter((item) => {
			let found = false;
			keysToSearch.forEach((_key) => {
				const keyArray = _key.split(".");
				const value = keyArray.reduce((acc, cur) => acc?.[cur], item);
				const hits = [];
				if (value) {
					if (strict) {
						searchString.split(" ").forEach((word) => {
							if (value.toLowerCase().includes(word.toLowerCase())) {
								hits.push(true);
							}
							else {
								hits.push(false);
							}
						});
						if (!hits.includes(false)) {
							found = true;
						}
					}
					else {
						searchString.split(" ").forEach((word) => {
							if (value.toLowerCase().includes(word.toLowerCase())) {
								found = true;
							}
						});
					}
				}
			});
			return found;
		});
	}
};

export const getTableComparator = (order, orderBy) => {
	const descendingComparator = (a, b, orderBy) => {
		if (b[orderBy] < a[orderBy]) {
			return -1;
		}
		if (b[orderBy] > a[orderBy]) {
			return 1;
		}
		return 0;
	};
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
};

export const uploadImage = async(files, token) => {
	if (files != null) {
		let server = "https://dev.service.youtello.com/v1/files/upload";
		if (process.env.NEXT_PUBLIC_ENVIRONMENT === "production") {
			server = "https://service.youtello.com/v1/files/upload";
		}
		else if (process.env.NEXT_PUBLIC_ENVIRONMENT === "staging") {
			server = "https://staging.service.youtello.com/v1/files/upload";
		}

		const formData = new FormData();
		formData.append("files", files[0]);
		const result = await fetch(
			server,
			{
				method: "POST",
				body: formData,
				headers: {
					Authorization: "Bearer " + token
				}
			});
		return (await result.json());
	}
};

export const JSONtoCSV = async(json, filename) => {
	if (
		!Array.isArray(json) ||
			!json.every((p) => typeof p === "object" && p !== null)
	) {
		return;
	}
	const heading = Object.keys(json[0]).join(";");
	const body = json.map((j) => Object.values(j).join(";")).join("\n");
	const data = `${heading}\n${body}`;
	saveAs(new Blob([data], { type: "text/csv" }), filename);
	return data;
};

/**
 * This algorithm is called the Modulus 11 algorithm, and produces a checksum
 * based on its input. It's documented very well in Norwegian here:
 * https://no.wikipedia.org/wiki/MOD11
 *
 * It's used here to validate that an organization number is valid.
 */
const calculateChecksum = (reversedNumbers) => {
	const controlSum = reversedNumbers.map((number, i) => number * ((i % 6) + 2)).reduce((a, b) => a + b, 0);
	const result = 11 - (controlSum % 11);
	return result === 11 ? 0 : result;
};

/**
 * Sjekker om verdien validerer som et gyldig organisasjonsnummer etter Modulus 11-algoritmen (https://no.wikipedia.org/wiki/MOD11).
 * @param value Verdien som skal valideres
 * @returns Om verdien er et gyldig organisasjonsnummer
 */
export const isValidOrganisasjonsnummer = (value) => {
	if (!value || value.length !== 9) {
		return false;
	}

	const [controlDigit, ...numbers] = value
		.split("")
		.map((number) => Number(number))
		.reverse();

	return calculateChecksum(numbers) === controlDigit;
};

export const truncate = (str, n) => {
	if (str?.length <= n) return str; // Nothing to do
	if (n <= 1) return "…"; // Well... not much else we can return here!
	const dot = str?.lastIndexOf(" "); // Splitter på siste space
	// How many characters from the end should remain:
	const after = dot < 0 ? 5 : Math.max(1, Math.min(n - 2, str?.length - dot + 0));
	// How many characters from the start should remain:
	const before = n - after - 1; // Account for the ellipsis
	return str?.slice(0, before) + "…" + str?.slice(-after);
};

/**
 * Checks if a value is not null or empty.
 * A value is considered null if it is strictly equal to null or undefined.
 * A value is considered empty if it has a length property that is 0.
 *
 * @param {any} value - The value to check.
 * @returns {boolean} - Returns true if the value is not null or empty, otherwise false.
 */
export const isNotNullOrEmpty = (value) => value && value.length > 0;

/**
 * Maps react-i18next language codes to corresponding country flag codes.
 * that matches the country flag codes used in the MUITelInput component.
 * See PhoneTextField component
 * @param {string} reactI18nextCode - The language code from react-i18next.
 * @returns {string} - The corresponding country flag code.
 */
export const getFlagCDNCode = (reactI18nextCode) => {
	const languageToFlagMap = {
		// Mapped languages
		nb: "no", // Norwegian Bokmål
		nn: "no", // Norwegian Nynorsk
		gsw: "ch", // Swiss German
		lb: "lu", // Luxembourgish
		be: "by", // Belarusian
		sr: "rs", // Serbian
		"ro-MD": "md", // Moldovan
		tl: "ph", // Filipino/Tagalog
		bs: "ba", // Bosnian
		mk: "mk", // Macedonian
		hr: "hr", // Croatian
		eo: "un", // Esperanto (fallback to UN or symbolic flag)
		gd: "gb", // Scots Gaelic
		cy: "gb", // Welsh
		id: "id", // Indonesian
		is: "is", // Icelandic
		en: "gb", // English - default to the UK
		"en-US": "us", // English (United States)
		"en-GB": "gb", // English (United Kingdom)
		"pt-BR": "br", // Portuguese (Brazil)
		sv: "se", // Swedish
		da: "dk", // Danish
		zh: "cn", // Chinese
		"zh-CN": "cn", // Simplified Chinese
		"zh-TW": "tw", // Traditional Chinese (Taiwan)
		ja: "jp", // Japanese
		ko: "kr", // Korean
		ar: "sa", // Arabic - default to Saudi Arabia
		hi: "in", // Hindi - India
		cs: "cz", // Czech
		el: "gr", // Greek
		uk: "ua" // Ukrainian
		// Add other mappings as needed
	};

	const code = languageToFlagMap[reactI18nextCode] || reactI18nextCode;
	return code.toUpperCase();
};

export function shallowEqualityCheck(obj1, obj2) {
	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);
	if (keys1.length !== keys2.length) {
		return false;
	}
	for (const key of keys1) {
		// console.log("T: ", (obj1[key] === obj2[key]), obj1[key], obj2[key]);
		if (obj1[key] !== obj2[key]) {
			return false;
		}
	}
	return true;
}


export const stringCase = (str) => {
	const splitStr = str?.toLowerCase().split(" ");
	for (let i = 0; i < splitStr?.length; i++) {
		// Assign it back to the array
		splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
	}
	// Directly return the joined string
	return splitStr?.join(" ");
};

export function isEmptyObject(object) {
	if (object === null || object === undefined) {
		return true;
	}
	return !Object.entries(object)
		.filter(([key]) => key !== "__typename") // Ignore __typename
		.some(([, value]) => value !== null && value !== undefined && value !== ""); // Check if any key has a valid value
};

export function dateDiffInSeconds(date) {
	const now = new Date();
	const givenDate = new Date(date);
	const diffInSeconds = Math.abs((now - givenDate) / 1000);
	return diffInSeconds;
}
